import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { Observable, take } from "rxjs";

import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { PermitStepSelectors } from "../../../permit/states/permit-step.selectors";
import { Permit } from "../../models/permit.model";
import { WorkplaceDetails } from "../../models/workplace-details.model";
import { PermitCreateActions } from "../../states/permit-create.actions";
import { PermitCreateSelectors } from "../../states/permit-create.selectors";

@UntilDestroy()
@Component({
    selector: "pmt-permit-additional-information",
    templateUrl: "./permit-additional-information.component.html",
    styleUrls: ["./permit-additional-information.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitAdditionalInformationComponent implements OnInit {
    @Select(PermitCreateSelectors.permit)
    public permit$: Observable<Permit>;

    @Select(PermitCreateSelectors.workplaceDetails)
    public workplaceDetails$: Observable<WorkplaceDetails>;

    public form = this.fb.group({
        permitAdditionalInformation: ["", Validators.maxLength(100)],
    });

    constructor(private fb: NonNullableFormBuilder, private store: Store, private actions$: Actions) {}

    public ngOnInit(): void {
        const permit = this.store.selectSnapshot(PermitCreateSelectors.permit);
        if (permit?.permitAdditionalInformation) {
            this.form.patchValue({ permitAdditionalInformation: permit.permitAdditionalInformation });
        }

        this.workplaceDetails$.pipe(untilDestroyed(this)).subscribe((workplaceDetails) => {
            const control = this.form.controls.permitAdditionalInformation;

            if (workplaceDetails?.additionalInformationRequired) {
                control.addValidators(Validators.required);
            } else {
                control.removeValidators(Validators.required);
            }

            control.updateValueAndValidity({ emitEvent: false });
        });

        this.actions$.pipe(ofActionSuccessful(PermitCreateActions.SetPermitAdditionalInformation))
            .pipe(untilDestroyed(this), take(1))
            .subscribe(() => {
                this.store.dispatch(new PermitStepActions.GoForward());
            });
    }

    public continue(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.store.dispatch(
            new PermitCreateActions.SetPermitAdditionalInformation(
                this.store.selectSnapshot(PermitStepSelectors.permitId),
                this.form.controls.permitAdditionalInformation.value,
            ),
        );
    }

    public goBack(): void {
        this.store.dispatch(new PermitStepActions.GoBackwards());
    }
}
